import {matchPath} from 'react-router'

import {AttendanceInputSuggestion} from '../attendance/attendanceInputSuggestions'

/**
 * The Da1Timetable (selective AI) help bot's screens: which screen a route is, sent as
 * `options.pageContext.page.id` so the answer is worded for it.
 *
 * The ids are the contract with node-api (`components/chatbot/aiHelp/screens.js`); the route
 * patterns are the portal's own (`SchoolRoutes.tsx`, `SchedulePage.tsx`, `UserManagementPage.tsx`).
 * AI credits have no route of their own (a header modal), so `credits` is never sent.
 */
type AiHelpScreen = {
  id: string
  /** react-router patterns; a trailing `/*` also matches the route itself. First match wins. */
  paths: string[]
}

const TIMETABLE = '/institute/schedule/timetable'

const AI_HELP_SCREENS: AiHelpScreen[] = [
  {
    id: 'dashboard',
    paths: ['/dashboard'],
  },
  {
    id: 'timetable.new_version',
    paths: [`${TIMETABLE}/new-version/*`],
  },
  {
    id: 'timetable.constraints',
    // Any `?step=`: the query string is never part of the pathname.
    paths: [`${TIMETABLE}/constraints/*`],
  },
  {
    id: 'timetable.visualization',
    paths: [`${TIMETABLE}/visualization/*`],
  },
  {
    id: 'timetable.workload_breakdown',
    paths: [`${TIMETABLE}/workload-breakdown/*`],
  },
  {
    id: 'timetable.teacher_workload',
    paths: [`${TIMETABLE}/teacher-workload/*`],
  },
  {
    id: 'timetable.view',
    paths: [`${TIMETABLE}/view/*`],
  },
  {
    id: 'timetable.picker',
    paths: [TIMETABLE],
  },
  {id: 'data.students', paths: ['/institute/student/*']},
  {
    id: 'data.teachers',
    paths: ['/institute/teacher/*'],
  },
  {
    id: 'data.grades',
    paths: ['/institute/grades/*'],
  },
  {id: 'data.sections', paths: ['/institute/sections/*']},
  {
    id: 'data.classes',
    paths: ['/institute/classes/*'],
  },
  {
    id: 'data.subjects',
    paths: ['/institute/subjects/*'],
  },
  {
    id: 'data.excel_import',
    paths: ['/institute/data-import/csv-import/*'],
  },
  {
    id: 'support.tickets',
    paths: ['/institute/support-tickets/*'],
  },
  {
    id: 'profile',
    paths: ['/profile/*'],
  },
  {
    id: 'users',
    // User Management > Users on these domains; the classic staff list too.
    paths: ['/institute/user-management/users/*', '/institute/staff/*'],
  },
  {
    id: 'roles_permissions',
    paths: ['/institute/user-management/roles-permissions/*'],
  },
]

const screenFor = (pathname: string): AiHelpScreen | null =>
  AI_HELP_SCREENS.find((screen) =>
    screen.paths.some((pattern) => matchPath({path: pattern, end: true}, pathname) !== null)
  ) ?? null

/** The help bot's screen id for this route (`timetable.constraints`, …), or null when unmapped. */
export const aiHelpScreenId = (pathname: string): string | null => screenFor(pathname)?.id ?? null

/**
 * The welcome's starter chips: the same on every screen, so Versi greets alike however it was
 * opened (the "Create a support ticket" action is appended after them by the page). Each is a
 * user manual section title, sent as the message exactly as written: the manual is English-only
 * and the backend finds the section by its title, so they are not translated.
 */
const WELCOME_CHIPS = ['Add a teacher', 'Generate a timetable', 'Import data from Excel']

export function getAiHelpWelcomeSuggestions(): AttendanceInputSuggestion[] {
  return WELCOME_CHIPS.map((chip, index) => ({
    id: `ai-help-welcome-${index}`,
    label: chip,
    text: chip,
    icon: 'support',
  }))
}
